import api from "../axios";

export type ResponseStatus = "PENDING" | "SUCCESS" | "FAILED" | "IN_PROGRESS" | "PARTIAL";

export interface MlQuery {
  id: string;
  tenantId: string;
  query: string;
  description: string;
  type: string;
  category?: string;
  riskType?: string;
  priority?: number;
  response?: string;
  responseStatus: ResponseStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMlQueryData {
  tenantId: string;
  query: string;
  description: string;
  type: string;
  category?: string;
  riskType?: string;
  priority?: number;
  response?: string;
}

export interface UpdateMlQueryData {
  query?: string;
  description?: string;
  type?: string;
  category?: string;
  riskType?: string;
  priority?: number;
  response?: string;
  responseStatus?: ResponseStatus;
  isActive?: boolean;
}

export interface MlQueryParams {
  skip?: number;
  take?: number;
  tenantId?: string;
  category?: string;
  status?: string;
  type?: string;
}

// ML Queries Service Class
export class MlQueriesService {
  // Basic CRUD Operations
  async createMlQuery(data: CreateMlQueryData): Promise<MlQuery> {
    const response = await api.post<MlQuery>("/ml-queries", data);
    return response.data;
  }

  async getMlQueries(params?: MlQueryParams): Promise<MlQuery[]> {
    const queryParams = new URLSearchParams();

    if (params?.skip) queryParams.append("skip", params.skip.toString());
    if (params?.take) queryParams.append("take", params.take.toString());
    if (params?.tenantId) queryParams.append("tenantId", params.tenantId);

    const url = `/ml-queries${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await api.get<MlQuery[]>(url);
    return response.data;
  }

  async getMlQueryById(id: string): Promise<MlQuery> {
    const response = await api.get<MlQuery>(`/ml-queries/${id}`);
    return response.data;
  }

  async updateMlQuery(id: string, data: UpdateMlQueryData): Promise<MlQuery> {
    const response = await api.patch<MlQuery>(`/ml-queries/${id}`, data);
    return response.data;
  }

  async deleteMlQuery(id: string): Promise<void> {
    await api.delete(`/ml-queries/${id}`);
  }

  async getMlQueriesByTenant(tenantId: string): Promise<MlQuery[]> {
    const response = await api.get<MlQuery[]>(`/ml-queries/tenant/${tenantId}`);
    return response.data;
  }

  async getMlQueriesByStatus(status: string): Promise<MlQuery[]> {
    const response = await api.get<MlQuery[]>(`/ml-queries/status/${status}`);
    return response.data;
  }

  async updateMlQueryStatus(
    id: string,
    status: ResponseStatus,
    response?: string
  ): Promise<MlQuery> {
    const updateData = { status, response };
    const result = await api.patch<MlQuery>(`/ml-queries/${id}/status`, updateData);
    return result.data;
  }

  // Risk Parameters specific methods (RISK_PARAMETERS category)
  async getRiskParameters(params?: Omit<MlQueryParams, 'category'>): Promise<MlQuery[]> {
    const riskParams = { ...params, category: 'RISK_PARAMETERS' };
    return this.getMlQueries(riskParams);
  }

  async createRiskParameter(data: Omit<CreateMlQueryData, 'category'>): Promise<MlQuery> {
    const riskData = { ...data, category: 'RISK_PARAMETERS' };
    return this.createMlQuery(riskData);
  }

  async updateRiskParameter(id: string, data: UpdateMlQueryData): Promise<MlQuery> {
    return this.updateMlQuery(id, data);
  }

  async deleteRiskParameter(id: string): Promise<void> {
    return this.deleteMlQuery(id);
  }

  async deleteAllRiskParametersForTenant(tenantId: string): Promise<void> {
    // First get all risk parameters for the tenant
    const riskParameters = await this.getRiskParameters({ tenantId });
    
    // Delete them one by one
    const deletePromises = riskParameters.map(param => this.deleteMlQuery(param.id));
    await Promise.all(deletePromises);
  }
}

// Create and export singleton instance
export const mlQueriesService = new MlQueriesService();

// Export individual methods for convenience
export const {
  createMlQuery,
  getMlQueries,
  getMlQueryById,
  updateMlQuery,
  deleteMlQuery,
  getMlQueriesByTenant,
  getMlQueriesByStatus,
  updateMlQueryStatus,
  getRiskParameters,
  createRiskParameter,
  updateRiskParameter,
  deleteRiskParameter,
  deleteAllRiskParametersForTenant,
} = mlQueriesService;
